import React from 'react';
import type { TFunction } from 'i18next';
import {
  ShieldCheck,
  Code,
  Mail,
  BarChart3,
  AlertTriangle,
  ChartNoAxesCombined,
  FileBarChart,
  Wrench,
  GraduationCap,
  ClipboardList,
  Clock,
  Users,
  Bug,
  Cloud,
  Laptop,
  Map,
  ClipboardCheck,
} from 'lucide-react';

export const DEFAULT_SLA_DURATIONS = {
  CRITICAL: {
    hours: 168,
    days: 7,
  },
  HIGH: {
    hours: 336,
    days: 14,
  },
  MEDIUM: {
    hours: 1440,
    days: 60,
  },
  LOW: {
    hours: 4320,
    days: 180,
  },
} as const;

export const DEFAULT_SLA_THRESHOLDS = {
  CRITICAL: {
    hours: 24,
    days: 1,
  },
  HIGH: {
    hours: 72,
    days: 3,
  },
  MEDIUM: {
    hours: 168,
    days: 7,
  },
  LOW: {
    hours: 336,
    days: 14,
  },
} as const;

export interface LibraryItem {
  id: string;
  title: string;
  description: string;
  prompt: string;
  icon: React.ReactNode;
  autoSubmit: boolean;
  category: string;
  tags: string[];
  featured?: boolean;
  chatPageCategory?: string; // if this is not defined then the prompt will not be shown on chat page
  dashboardId?: string; // if present, clicking will navigate to dashboard instead of submitting prompt
  dashboardTitleMatch?: string; // if dashboardId is not set, use this to find dashboard by title
}

export interface LibraryGroup {
  id: string;
  name: string;
  description: string;
  items: LibraryItem[];
}

export const libraryItems = (t: TFunction<'pleri/library'>): LibraryItem[] => [
  // Questions - Featured examples
  {
    id: 'approaching-sla',
    title: t('vulnerabilitiesApproachingSla'),
    description: t('areThereAnyVulnerabilitiesApproachingSla'),
    prompt: `Are there any vulnerabilities in our cloud environments that are approaching SLA?

Our SLA policies:
- Critical: ${DEFAULT_SLA_DURATIONS.CRITICAL.hours} hours (${DEFAULT_SLA_DURATIONS.CRITICAL.days} days)
- High: ${DEFAULT_SLA_DURATIONS.HIGH.hours} hours (${DEFAULT_SLA_DURATIONS.HIGH.days} days)
- Medium: ${DEFAULT_SLA_DURATIONS.MEDIUM.hours} hours (${DEFAULT_SLA_DURATIONS.MEDIUM.days} days)
- Low: ${DEFAULT_SLA_DURATIONS.LOW.hours} hours (${DEFAULT_SLA_DURATIONS.LOW.days} days)

Warning thresholds (advance notice):
- Critical: ${DEFAULT_SLA_THRESHOLDS.CRITICAL.hours} hours (${DEFAULT_SLA_THRESHOLDS.CRITICAL.days} days) before due date
- High: ${DEFAULT_SLA_THRESHOLDS.HIGH.hours} hours (${DEFAULT_SLA_THRESHOLDS.HIGH.days} days) before due date
- Medium: ${DEFAULT_SLA_THRESHOLDS.MEDIUM.hours} hours (${DEFAULT_SLA_THRESHOLDS.MEDIUM.days} days) before due date
- Low: ${DEFAULT_SLA_THRESHOLDS.LOW.hours} hours (${DEFAULT_SLA_THRESHOLDS.LOW.days} days) before due date`,
    icon: <Bug className="h-5 w-5" />,
    autoSubmit: false,
    category: 'questions',
    tags: ['vulnerability', 'approaching', 'sla', 'template'],
    featured: true,
    chatPageCategory: 'vulnerabilities',
  },
  {
    id: 'vulnerability-management',
    title: t('vulnerabilityManagementTrends'),
    description: t('areWeGettingBetterOrWorseAt'),
    prompt: 'Are we getting better or worse at vulnerability management?',
    icon: <Bug className="h-5 w-5" />,
    autoSubmit: true,
    category: 'questions',
    tags: ['vulnerability', 'trends', 'management'],
    featured: true,
    chatPageCategory: 'vulnerabilities',
  },
  {
    id: 'breached-sla',
    title: t('vulnerabilitiesBreachedSla'),
    description: t('areThereAnyVulnerabilitiesBreachedSla'),
    prompt: 'Are there any vulnerabilities in our cloud environments that have breached SLA?',
    icon: <Bug className="h-5 w-5" />,
    autoSubmit: true,
    category: 'questions',
    tags: ['vulnerability', 'breached', 'sla'],
    featured: true,
    chatPageCategory: 'vulnerabilities',
  },
  // Assessments category
  {
    id: 'cloud-security-assessment',
    title: t('cloudSecurityAssessment'),
    description: t('runAComprehensiveCloudSecurityAssessment'),
    prompt: 'Run a comprehensive cloud security assessment.',
    icon: <ShieldCheck className="h-5 w-5" />,
    autoSubmit: true,
    category: 'tasks',
    tags: ['assessment', 'comprehensive', 'cloud'],
    featured: true,
    chatPageCategory: 'assessments',
  },
  {
    id: 'code-security-assessment',
    title: t('codeSecurityAssessment'),
    description: t('whereDoWeStandWithOurCode'),
    prompt: 'Run a code security assessment. Where do we stand with our code security?',
    icon: <Code className="h-5 w-5" />,
    autoSubmit: true,
    category: 'questions',
    tags: ['code', 'assessment'],
    featured: true,
    chatPageCategory: 'assessments',
  },
  {
    id: 'log4shell-assessment',
    title: t('log4ShellAssessment'),
    description: t('identifyAndMitigateRisksFromTheCritical'),
    prompt: 'Run a Log4Shell assessment',
    icon: <ShieldCheck className="h-5 w-5" />,
    autoSubmit: true,
    category: 'tasks',
    tags: ['assessment', 'log4j', 'log4shell', 'rce'],
    featured: true,
    chatPageCategory: 'assessments',
  },
  {
    id: 'react2shell-assessment',
    title: t('react2ShellAssessment'),
    description: t('react2ShellCve202555182IsAVulnerability'),
    prompt: `What is it React2Shell? How does it work, and how serious is it? Pull the key details from authoritative sources and include any real-world risk signals (sightings and EPSS). Then tell me if we're affected in our cloud and code.`,
    icon: <ShieldCheck className="h-5 w-5" />,
    autoSubmit: true,
    category: 'tasks',
    tags: [
      'assessment',
      'react',
      'react2shell',
      'cve-2025-55182',
      'vulnerability',
    ],
    featured: true,
    chatPageCategory: 'assessments',
  },
  {
    id: 'mongobleed-assessment',
    title: t('mongoBleedAssessment'),
    description: t('mongoBleedCve202514847IsARecently'),
    prompt: `What is MongoBleed? How does it work, and how serious is it? Pull the key details from authoritative sources and include any real-world risk signals (sightings and EPSS). Then tell me if we're affected in our cloud and code.`,
    icon: <ShieldCheck className="h-5 w-5" />,
    autoSubmit: true,
    category: 'tasks',
    tags: [
      'assessment',
      'mongodb',
      'cve-2025-14847',
      'vulnerability',
      'heap-memory',
    ],
    featured: true,
    chatPageCategory: 'assessments',
  },
  // Explore category
  {
    id: 'explore-public-assets',
    title: t('explorePublicAssetsExposure'),
    description: t('areThereAnyPubliclyExposedAssetsIn'),
    prompt: 'Explore public assets exposure in our cloud.',
    icon: <Cloud className="h-5 w-5" />,
    autoSubmit: true,
    category: 'questions',
    tags: ['public', 'assets', 'exposure', 'explore'],
    featured: true,
    chatPageCategory: 'explore',
  },
  {
    id: 'explore-vulnerabilities',
    title: t('exploreVulnerabilitiesInOurCloud'),
    description: t('exploreAndAnalyzeVulnerabilitiesInOurCloud'),
    prompt: 'Explore vulnerabilities in our cloud.',
    icon: <Bug className="h-5 w-5" />,
    autoSubmit: true,
    category: 'questions',
    tags: ['vulnerabilities', 'cloud', 'explore'],
    featured: true,
    chatPageCategory: 'explore',
  },
  {
    id: 'explore-findings',
    title: t('exploreFindingsMisconfigurationsInOurCloud'),
    description: t('exploreFindingsAndMisconfigurationsInOurCloud'),
    prompt: 'Explore findings/misconfigurations in our cloud.',
    icon: <AlertTriangle className="h-5 w-5" />,
    autoSubmit: true,
    category: 'questions',
    tags: ['findings', 'misconfigurations', 'cloud', 'explore'],
    featured: true,
    chatPageCategory: 'explore',
  },
  {
    id: 'explore-attack-paths',
    title: t('exploreAttackPathsInOurCloud'),
    description: t('areThereAnyAttackPathsInOur'),
    prompt: 'Explore attack paths in our cloud.',
    icon: <Map className="h-5 w-5" />,
    autoSubmit: true,
    category: 'questions',
    tags: ['attack-paths', 'cloud', 'security', 'explore'],
    featured: true,
    chatPageCategory: 'explore',
  },
  {
    id: 'soc2-controls',
    title: t('soc2ComplianceStatus'),
    description: t('areThereAnyControlsTestsFailingAgainst'),
    prompt: 'Are there any controls/tests failing against SOC 2?',
    icon: <FileBarChart className="h-5 w-5" />,
    autoSubmit: true,
    category: 'questions',
    tags: ['SOC2', 'compliance', 'controls'],
    featured: true,
    chatPageCategory: 'compliance',
  },

  // More Questions
  {
    id: 'well-architected',
    title: t('awsWellArchitected'),
    description: t('areWeWellArchitected'),
    prompt: 'Are we Well-Architected?',
    icon: <ShieldCheck className="h-5 w-5" />,
    autoSubmit: true,
    category: 'questions',
    tags: ['aws', 'architecture', 'well-architected'],
  },
  {
    id: 'regional-vulnerabilities',
    title: t('regionalVulnerabilities'),
    description: t('doWeHaveAnyVulnerabilitiesInThe'),
    prompt: 'Do we have any vulnerabilities in the Sydney region?',
    icon: <AlertTriangle className="h-5 w-5" />,
    autoSubmit: true,
    category: 'questions',
    tags: ['vulnerabilities', 'regional', 'sydney'],
    chatPageCategory: 'vulnerabilities',
  },
  {
    id: 'cve-remediation',
    title: t('cveRemediationHelp'),
    description: t('helpMeRemediateSpecificCVEs'),
    prompt: 'Help me remediate [CVE-ID].',
    icon: <Wrench className="h-5 w-5" />,
    autoSubmit: false,
    category: 'questions',
    tags: ['cve', 'remediation', 'template'],
    chatPageCategory: 'remediation',
  },
  {
    id: 'macos-versions',
    title: t('macOSCompliance'),
    description: t('isAnyoneNotRunningTheLatestVersion'),
    prompt: 'Is anyone not running the latest version of macOS?',
    icon: <Laptop className="h-5 w-5" />,
    autoSubmit: true,
    category: 'questions',
    tags: ['macos', 'compliance', 'endpoints'],
    chatPageCategory: 'compliance',
  },
  {
    id: 'guided-remediation',
    title: t('guidedDeveloperRemediation'),
    description: t('stepByStepRemediationGuidanceForDevelopers'),
    prompt: `I've got a finding I don't fully understand and I'm short on time. Review the findings for this Resource, identify what I should fix first

Current mitigation attempts: [brief notes or "none"]
Constraints: [eg change freeze window, need zero downtime, etc]

Walk me through a clear, step by step fix I can apply right now. Explain WHY each step matters and call out any safety checks or roll‑back tips. Limit jargon, link to any docs I should reference, and estimate effort in hours.`,
    icon: <GraduationCap className="h-5 w-5" />,
    autoSubmit: false,
    category: 'questions',
    tags: ['remediation', 'developer', 'template'],
    chatPageCategory: 'remediation',
  },
  {
    id: 'cab-change-plan',
    title: t('cabChangeManagement'),
    description: t('generateChangeAdvisoryBoardReadyImplementation'),
    prompt: `I need a CAB‑ready change plan for this remediation.
Finding IDs: [list]
Business objective: [eg maintain PCI compliance, no downtime]
Planned window: [dates / times]
Teams involved: [names / roles]

Generate:
1. High level change description and justification
2. Detailed implementation steps with owner, start‑finish time, rollback steps
3. Pre and post‑change validation checks
4. Communication plan – who gets notified and when
5. Risk assessment scored Low/Med/High with mitigation notes

Return in bulleted Markdown I can paste straight into the CAB ticket.`,
    icon: <ClipboardList className="h-5 w-5" />,
    autoSubmit: false,
    category: 'questions',
    tags: ['cab', 'change-management', 'template'],
    chatPageCategory: 'remediation',
  },
  {
    id: 'sprint-planning',
    title: t('sprintSecurityPlanning'),
    description: t('helpPrioritizeSecurityWorkForDevelopmentSprints'),
    prompt: `Help me prioritize security work for the next sprint.
Backlog: [CSV or bullet list of findings with severities]
Team capacity: [dev hours or headcount]
Desired outcome: [eg meet 80% SLA on High findings, no Sev1 open by Q4]
Timeline: [eg 2 weeks sprint, or Q3 roadmap]

Return a ranked backlog with:
- Effort estimate (S, M, L, XL)
- Business impact score
- Dependencies or blockers
- Recommended sprint placement (Now, Next, Later)

Finish with a burndown projection so I can see if the goal is realistic.`,
    icon: <Users className="h-5 w-5" />,
    autoSubmit: false,
    category: 'questions',
    tags: ['sprint', 'planning', 'template'],
    chatPageCategory: 'remediation',
  },
  {
    id: 'architecture-diagram',
    title: t('cloudArchitectureDiagram'),
    description: t('generateDetailedCloudArchitectureDiagrams'),
    prompt: `Generate an up‑to‑date cloud diagram that shows:
- Account Structure
- Public / private VPC and subnets
- All public‑facing endpoints and internet path
- Identity trust links and cross‑account roles
- Critical data stores with sensitivity level
- Potential blast radius if Asset [ARN or tag] is breached

Output format: Mermaid (simple, readable and detailed)
Scope: [All Account, Prod accounts]
Add a short paragraph that explains the biggest exposure paths in plain language for execs.`,
    icon: <BarChart3 className="h-5 w-5" />,
    autoSubmit: false,
    category: 'questions',
    tags: ['architecture', 'diagram', 'template'],
  },

  {
    id: 'gameoverlay-assessment',
    title: t('gameOverLayAssessment'),
    description: t('evaluateUbuntuSystemsForLocalPrivilegeEscalation'),
    prompt: 'Run a GameOver(lay) assessment',
    icon: <ShieldCheck className="h-5 w-5" />,
    autoSubmit: true,
    category: 'tasks',
    tags: ['assessment', 'overlay-fs', 'ubuntu', 'privilege-escalation'],
    featured: true,
  },
  {
    id: 'nvidia-triton-python-backend-assessment',
    title: t('nvidiaTritonPythonBackendAssessment'),
    description: t('checkForExposureToVulnerabilitiesInNvidia'),
    prompt: 'Run a NVIDIA Triton Python backend assessment',
    icon: <ShieldCheck className="h-5 w-5" />,
    autoSubmit: true,
    category: 'tasks',
    tags: ['assessment', 'nvidia', 'triton', 'ml', 'python-backend'],
    featured: true,
  },
  // What's new category
  {
    id: 'shai-hulud-npm-supply-chain-worm-assessment',
    title: t('shaiHuludNpmSupplyChainWormAssessment'),
    description: t('selfPropagatingWormCompromisingNpmPackages'),
    prompt: 'Run a Shai-Hulud npm supply-chain worm assessment',
    icon: <ShieldCheck className="h-5 w-5" />,
    autoSubmit: true,
    category: 'tasks',
    tags: ['assessment', 'shai-hulud', 'npm', 'supply-chain', 'worm'],
    featured: true,
    chatPageCategory: 'whats-new',
  },
  {
    id: 'shai-hulud-2.0-npm-supply-chain-worm-assessment',
    title: t('shaiHulud20NpmSupplyChain'),
    description: t('selfPropagatingShaiHulud20Npm'),
    prompt: 'Run a Shai-Hulud 2.0 npm supply-chain worm assessment',
    icon: <ShieldCheck className="h-5 w-5" />,
    autoSubmit: true,
    category: 'tasks',
    tags: [
      'assessment',
      'shai-hulud-2.0',
      'npm',
      'supply-chain',
      'worm',
      'malware',
    ],
    featured: true,
    chatPageCategory: 'whats-new',
  },
  {
    id: 'xz-utils-backdoor-assessment',
    title: t('xzUtilsBackdoorAssessment'),
    description: t('assessExposureToTheXzUtilsSupply'),
    prompt: 'Run a XZ Utils backdoor assessment',
    icon: <ShieldCheck className="h-5 w-5" />,
    autoSubmit: true,
    category: 'tasks',
    tags: ['assessment', 'xz-utils', 'supply-chain', 'backdoor'],
    featured: true,
    chatPageCategory: 'whats-new',
  },
  {
    id: 'time-efficient-plan',
    title: t('text4HourWeeklySecurityPlan'),
    description: t('maximizeImpactWithLimitedTimeAllocation'),
    prompt: 'I can only allocate four hours a week to support my cyber outcomes. Give me a plan to make the most impact over the next 3 months.',
    icon: <Clock className="h-5 w-5" />,
    autoSubmit: true,
    category: 'tasks',
    tags: ['planning', 'time-management', 'efficiency'],
    featured: true,
    chatPageCategory: 'task',
  },
  {
    id: 'download-vulnerabilities',
    title: t('downloadVulnerabilityReport'),
    description: t('sendMeAFileWithAllVulnerabilities'),
    prompt: 'Send me a file with all vulnerabilities.',
    icon: <Bug className="h-5 w-5" />,
    autoSubmit: true,
    category: 'tasks',
    tags: ['vulnerabilities', 'download', 'report'],
    chatPageCategory: 'vulnerabilities',
  },
  {
    id: 'severity-chart',
    title: t('misconfigurationSeverityChart'),
    description: t('makeAPieChartShowingOurMisconfigurations'),
    prompt: 'Make a pie chart showing our misconfigurations by severity.',
    icon: <BarChart3 className="h-5 w-5" />,
    autoSubmit: true,
    category: 'tasks',
    tags: ['chart', 'misconfigurations', 'severity'],
  },
  {
    id: 'code-repos-critical',
    title: t('criticalCodeVulnerabilities'),
    description: t('listCodeRepositoriesWithCriticalVulnerabilities'),
    prompt: 'List code repositories with critical vulnerabilities.',
    icon: <Code className="h-5 w-5" />,
    autoSubmit: true,
    category: 'tasks',
    tags: ['code', 'repositories', 'critical'],
  },
  {
    id: 'fix-js-package-vulnerabilities',
    title: t('fixJavaScriptDependencyVulnerabilities'),
    description: t('fixesJavaScriptDependencyVulnerabilitiesBy'),
    prompt: `Let's create a task to fix my vulnerable JavaScript dependencies.`,
    icon: <Code className="h-5 w-5" />,
    autoSubmit: true,
    category: 'tasks',
    tags: ['vulnerabilities', 'code', 'repositories', 'lockfile'],
    chatPageCategory: 'task',
  },
  {
    id: 'vanta-tests',
    title: t('vantaTestStatus'),
    description: t('listVantaTestsThatStillNeedAttention'),
    prompt: 'List Vanta tests that still need attention.',
    icon: <FileBarChart className="h-5 w-5" />,
    autoSubmit: true,
    category: 'tasks',
    tags: ['vanta', 'tests', 'compliance'],
  },
  {
    id: 'risk-email-boss',
    title: t('riskEmailToLeadership'),
    description: t('emailBiggestCloudSecurityRiskToLeadership'),
    prompt: 'Email my boss [email@example.com] our biggest cloud security risk right now. Copy me.',
    icon: <Mail className="h-5 w-5" />,
    autoSubmit: false,
    category: 'tasks',
    tags: ['email', 'risk', 'leadership'],
  },

  // Task
  {
    id: 'weekly-cloud-check',
    title: t('weeklyCloudMonitoring'),
    description: t('mondayMorningCloudEnvironmentScanAndSummary'),
    prompt: 'Every Monday at 8am (Australia/Sydney), scan our cloud environment and email me a summary of what changed, new risks, resolved issues, and any important shifts in posture.',
    icon: <Cloud className="h-5 w-5" />,
    autoSubmit: true,
    category: 'tasks',
    tags: ['weekly', 'monitoring', 'cloud'],
    featured: true,
    chatPageCategory: 'task',
  },
  {
    id: 'iam-user-review',
    title: t('monthlyIamReview'),
    description: t('lastFridayMonthlyReviewOfIamUsers'),
    prompt: 'On the last Friday of every month at 8am, review IAM users across AWS. If you see inactive accounts, missing MFA, or risky access patterns, flag them.',
    icon: <Users className="h-5 w-5" />,
    autoSubmit: true,
    category: 'tasks',
    tags: ['monthly', 'iam', 'review'],
    featured: true,
    chatPageCategory: 'task',
  },
  {
    id: 'macos-updates-check',
    title: t('macOSUpdateMonitoring'),
    description: t('mondayMorningMacOSVersionComplianceCheck'),
    prompt: `Monday mornings, check if everyone's on the latest macOS. If not, nudge them in Slack so they can update their laptops.`,
    icon: <Laptop className="h-5 w-5" />,
    autoSubmit: true,
    category: 'tasks',
    tags: ['weekly', 'macos', 'compliance'],
    chatPageCategory: 'task',
  },
  {
    id: 'compliance-daily-check',
    title: t('dailyComplianceMonitoring'),
    description: t('endOfDayComplianceControlStatusCheck'),
    prompt: 'Email me if any compliance controls are still failing by end of day.',
    icon: <FileBarChart className="h-5 w-5" />,
    autoSubmit: true,
    category: 'tasks',
    tags: ['daily', 'compliance', 'monitoring'],
    chatPageCategory: 'task',
  },
  {
    id: 'weekly-mfa-check',
    title: t('weeklyMfaEnforcement'),
    description: t('weeklyCheckForIamUsersWithoutMfa'),
    prompt: 'Weekly check: flag any IAM users without MFA enabled.',
    icon: <ShieldCheck className="h-5 w-5" />,
    autoSubmit: true,
    category: 'tasks',
    tags: ['weekly', 'mfa', 'security'],
    chatPageCategory: 'task',
  },
  {
    id: 'monthly-unused-access',
    title: t('monthlyAccessReview'),
    description: t('reviewPermissionsThatHavenBeenUsedIn'),
    prompt: `Monthly, review IAM roles and users for permissions that haven't been used in the last 90 days.`,
    icon: <Wrench className="h-5 w-5" />,
    autoSubmit: true,
    category: 'tasks',
    tags: ['monthly', 'access', 'cleanup'],
    chatPageCategory: 'task',
  },
];

// Dashboard items - these navigate to dashboards, not prompts
export const dashboardItems = (t: TFunction<'pleri/library'>): LibraryItem[] => [
  {
    id: 'cloud-security-dashboard',
    title: t('cloudSecurityDashboard'),
    description: t('viewComprehensiveCloudSecurityMetricsAndInsights'),
    prompt: '', // Not used for dashboard items
    icon: <ShieldCheck className="h-5 w-5" />,
    autoSubmit: false,
    category: 'dashboards',
    tags: ['dashboard', 'cloud', 'security'],
    featured: true,
    chatPageCategory: 'dashboards',
    dashboardTitleMatch: 'Cloud security',
  },
  {
    id: 'sbom-dashboard',
    title: t('softwareBillOfMaterialsSbomDashboards'),
    description: t('viewSoftwareBillOfMaterialsDashboardsAnd'),
    prompt: '', // Not used for dashboard items
    icon: <ChartNoAxesCombined className="h-5 w-5" />,
    autoSubmit: false,
    category: 'dashboards',
    tags: ['dashboard', 'sbom', 'software', 'bill-of-materials'],
    featured: true,
    chatPageCategory: 'dashboards',
    dashboardTitleMatch: 'SBOM',
  },
  {
    id: 'sbom-dashboard-whats-new',
    title: t('softwareBillOfMaterialsSbomDashboards'),
    description: t('viewSoftwareBillOfMaterialsDashboardsAnd'),
    prompt: '', // Not used for dashboard items
    icon: <ChartNoAxesCombined className="h-5 w-5" />,
    autoSubmit: false,
    category: 'dashboards',
    tags: ['dashboard', 'sbom', 'software', 'bill-of-materials'],
    featured: true,
    chatPageCategory: 'whats-new',
    dashboardTitleMatch: 'SBOM',
  },
];

export const libraryGroups = (t: TFunction<'pleri/library'>): LibraryGroup[] => [
  {
    id: 'questions',
    name: t('common:questions'),
    description: t('iCanAnswerAlmostAnyQuestionAbout'),
    items: libraryItems(t).filter((item) => item.category === 'questions'),
  },
  {
    id: 'tasks',
    name: t('tasks'),
    description: t('iGreatAtGettingRoutineWorkDone'),
    items: libraryItems(t).filter((item) => item.category === 'tasks'),
  },
];

export interface ChatPageCategory {
  id: string;
  name: string;
  icon: React.ReactNode;
}

export const chatPageCategories = (t: TFunction<'pleri/library'>): ChatPageCategory[] => [
  {
    id: 'explore',
    name: t('explore'),
    icon: <Map className="h-4 w-4" />,
  },
  {
    id: 'assessments',
    name: t('assessments'),
    icon: <ClipboardCheck className="h-4 w-4" />,
  },
  {
    id: 'dashboards',
    name: t('dashboards'),
    icon: <ChartNoAxesCombined className="h-4 w-4" />,
  },
  {
    id: 'whats-new',
    name: t('whatNew'),
    icon: <Clock className="h-4 w-4" />,
  },
];
